import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, Phone, MessageCircle, ChevronDown, ChevronUp,
  Clock, ArrowRight, Star, MapPin, Shield, Tag,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import ContactFormWidget from '../components/ContactFormWidget';
import { PHONE, PHONE_LINK, WHATSAPP, DOMAIN, P } from '../constants';

const NOE_CITIES = [
  'Klosterneuburg', 'Korneuburg', 'Stockerau', 'Tulln an der Donau',
  'Krems an der Donau', 'St. Pölten', 'Mödling', 'Baden bei Wien',
  'Wiener Neustadt', 'Schwechat', 'Perchtoldsdorf', 'Brunn am Gebirge',
  'Traiskirchen', 'Bruck an der Leitha', 'Amstetten', 'Hollabrunn',
  'Mistelbach', 'Gänserndorf', 'Neunkirchen', 'Wr. Neustadt Umgebung',
];

const SERVICES_NOE = [
  { title: 'Wohnungsräumung', desc: 'Wohnungen aller Größen – vollständig geräumt, besenrein übergeben.', slug: '/wohnungsraeumung-wien' },
  { title: 'Haushaltsauflösung', desc: 'Komplette Auflösung mit Wertanrechnung für brauchbare Gegenstände.', slug: '/haushaltsaufloesung-wien' },
  { title: 'Nachlassräumung', desc: 'Diskret, einfühlsam und ohne Zeitdruck in schwierigen Momenten.', slug: '/nachlassraeumung-wien' },
  { title: 'Kellerräumung', desc: 'Keller und Dachböden in ganz NÖ – schnell und fachgerecht entsorgt.', slug: '/kellerraeumung-wien' },
  { title: 'Dachbodenräumung', desc: 'Jahrzehnte an Gerümpel – wir tragen alles runter und entsorgen.', slug: '/dachbodenraeumung-wien' },
  { title: 'Büroräumung', desc: 'Büros und Gewerberäume – effizient, diskret, zum Fixpreis.', slug: '/bueroraeumung-wien' },
];

const FAQS = [
  { q: 'Fahren Sie auch nach Niederösterreich?', a: 'Ja, wir sind in ganz Niederösterreich tätig – von Klosterneuburg und Tulln im Norden bis Wiener Neustadt und Baden im Süden, und von St. Pölten im Westen bis Schwechat und Bruck an der Leitha im Osten.' },
  { q: 'Gibt es einen Aufpreis für die Anfahrt nach NÖ?', a: 'Nein. Die Anfahrt ist bei uns im Fixpreis inkludiert. Es gibt keine versteckten Fahrtkosten. Nach der kostenlosen Besichtigung erhalten Sie einen verbindlichen Gesamtpreis – egal wo in NÖ.' },
  { q: 'Was kostet eine Räumung in Niederösterreich?', a: 'Die Preise sind dieselben wie in Wien: Keller / kleine Objekte ab €290, Wohnungen ab €490, größere Häuser ab €890. Verbindlicher Fixpreis nach kostenloser Besichtigung vor Ort.' },
  { q: 'Wie schnell können Sie in Niederösterreich räumen?', a: 'Sehr schnell. In den meisten Fällen sind wir noch diese Woche verfügbar. Für Notfälle – z.B. bei laufendem Mietvertrag oder Todesfall – reagieren wir bevorzugt.' },
  { q: 'Welche Leistungen bieten Sie in ganz NÖ an?', a: 'Wir bieten in ganz Niederösterreich Wohnungsräumung, Haushaltsauflösung, Nachlassräumung, Keller- und Dachbodenräumung sowie Büroräumung an – alles aus einer Hand, zum Fixpreis.' },
  { q: 'Muss ich bei der Räumung in NÖ dabei sein?', a: 'Nicht zwingend. Viele Kunden übergeben uns einfach den Schlüssel. Wir arbeiten sorgfältig und übergeben das Objekt besenrein zurück – egal ob in Wien oder irgendwo in Niederösterreich.' },
];

const schemas = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Räumung Niederösterreich',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': ['Wien', 'Niederösterreich'],
    'description': 'Professionelle Wohnungsräumung, Haushaltsauflösung und Entrümpelung in ganz Niederösterreich. Fixpreisgarantie und besenreine Übergabe.',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Startseite', 'item': DOMAIN },
      { '@type': 'ListItem', 'position': 2, 'name': 'Räumung Niederösterreich', 'item': `${DOMAIN}/raeumung-niederoesterreich` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': FAQS.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a },
    })),
  },
];

export default function NiederoesterreichPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <SEOHead
        title="Räumung Niederösterreich » Wohnungsräumung & Entrümpelung NÖ – Fixpreis"
        description="Räumung Niederösterreich ✓ Wohnungsräumung, Haushaltsauflösung & Entrümpelung in ganz NÖ ✓ Kein Anfahrtsaufpreis ✓ Fixpreis ✓ Klosterneuburg, Mödling, Wiener Neustadt, Baden & mehr."
        keywords="Räumung Niederösterreich, Wohnungsräumung Niederösterreich, Entrümpelung Niederösterreich, Haushaltsauflösung NÖ, Räumung NÖ, Entrümpelung NÖ"
        canonical="/raeumung-niederoesterreich"
        extraSchemas={schemas}
      />

      <main>
        {/* BREADCRUMB */}
        <nav className="bg-[#FAFAF8] border-b border-gray-200 py-2 px-4" aria-label="Breadcrumb">
          <div className="max-w-7xl mx-auto">
            <ol className="flex items-center gap-2 text-xs text-gray-500">
              <li><Link to="/" className="hover:text-[#E8722A] transition-colors">Startseite</Link></li>
              <li className="text-gray-300">/</li>
              <li className="text-[#1A1A2E] font-semibold">Räumung Niederösterreich</li>
            </ol>
          </div>
        </nav>

        {/* HERO */}
        <section
          className="relative flex items-center"
          style={{
            backgroundImage: `url('${P.clutteredRoom}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '88vh',
          }}
        >
          <div className="absolute inset-0 bg-[#1A1A2E]/85" />
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-14 lg:py-20">
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-[#E8722A] text-white px-4 py-2 rounded text-xs font-black mb-5 uppercase tracking-wider">
                  <MapPin size={13} /> Wien & ganz Niederösterreich
                </div>
                <h1 className="cabinet text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-4">
                  <span>Räumung<br />Niederösterreich</span><br />
                  <span className="text-[#E8722A]">zum Fixpreis.</span>
                </h1>
                <p className="text-lg text-gray-200 font-semibold mb-4">
                  Von Klosterneuburg bis Wiener Neustadt. Wir kommen zu Ihnen.
                </p>
                <div className="space-y-2.5 mb-8">
                  {[
                    'Kein Aufpreis für die Anfahrt – Fixpreis inklusive Anreise',
                    'Wohnungen, Keller, Dachböden, Büros – alles aus einer Hand',
                    'Kurzfristige Termine in ganz NÖ – oft noch diese Woche',
                    'Wertanrechnung: Brauchbares direkt vom Preis abgezogen',
                    'Besenreine Übergabe. Immer. Ohne Aufpreis.',
                  ].map((b) => (
                    <div key={b} className="flex items-start gap-2.5">
                      <CheckCircle size={18} className="text-[#E8722A] flex-shrink-0 mt-0.5" />
                      <span className="text-white/90 text-sm leading-snug">{b}</span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <a href={PHONE_LINK}
                    className="flex items-center gap-2 bg-white text-[#1A1A2E] font-black px-6 py-3.5 rounded-lg hover:bg-gray-100 transition-colors text-sm">
                    <Phone size={16} /> Jetzt anrufen
                  </a>
                  <a href={WHATSAPP} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 bg-[#25D366] text-white font-black px-6 py-3.5 rounded-lg hover:bg-[#20bd5a] transition-colors text-sm">
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                </div>
              </div>
              <div id="kontakt" className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
                <div className="mb-5">
                  <h2 className="cabinet font-bold text-[#1A1A2E] leading-tight mb-1" style={{ fontSize: '1.15rem', letterSpacing: '-0.01em' }}>
                    Räumung in NÖ anfragen – <span className="text-[#E8722A]">in 2 Minuten</span>
                  </h2>
                  <p className="text-gray-500 text-xs">Kein Risiko. Kostenlose Besichtigung. Verbindlicher Fixpreis.</p>
                </div>
                <ContactFormWidget serviceName="Räumung Niederösterreich" />
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR */}
        <section className="bg-[#FAFAF8] border-y border-gray-200 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-200">
              {[
                { val: '⭐ 4.9 / 5.0', label: 'Google Bewertung' },
                { val: '2.000+ Kunden', label: 'Wien & NÖ seit 2010' },
                { val: 'Fixpreisgarantie', label: 'Kein Anfahrtsaufpreis' },
                { val: '24h Service', label: 'Notfälle bevorzugt' },
              ].map((t) => (
                <div key={t.label} className="py-3 px-4 text-center">
                  <div className="font-black text-[#1A1A2E] text-base sm:text-lg leading-tight">{t.val}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{t.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* INTRO */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-3">Professionell & zuverlässig</p>
                <h2 className="cabinet text-3xl lg:text-4xl font-bold text-[#1A1A2E] mb-5 leading-tight">
                  Räumung in ganz Niederösterreich – fair, schnell, besenrein
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Wohnungsübergabe, Haushaltsauflösung, Todesfall oder einfach Entrümpelung – egal wo in Niederösterreich: wir kommen zu Ihnen. Klosterneuburg, Mödling, Baden, Wiener Neustadt, St. Pölten oder Krems. Kein Aufpreis für die Anfahrt.
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Einmal anfragen. Wir fahren vorbei, schauen alles an und nennen Ihnen einen verbindlichen Fixpreis. Dann räumen wir – vollständig, diskret und mit besenreiner Übergabe.
                </p>
                <div className="space-y-2.5 mb-8">
                  {[
                    'Anfahrt in ganz NÖ immer im Preis inkludiert',
                    'Alle Leistungen verfügbar: Wohnung, Keller, Dachboden, Büro',
                    'Wertanrechnung für brauchbare Gegenstände',
                    'Sortenreine Entsorgung und umweltgerechte Verwertung',
                    'Besenreine Übergabe nach jeder Räumung',
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircle size={17} className="text-[#E8722A] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </div>
                  ))}
                </div>
                <a href={PHONE_LINK}
                  className="inline-flex items-center gap-2 bg-[#E8722A] hover:bg-[#d4651e] text-white font-black px-7 py-3.5 rounded-lg transition-colors text-sm">
                  <Phone size={15} /> {PHONE}
                </a>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-xl h-80 lg:h-[460px]">
                <img src={P.emptyRoom} alt="Geräumte Wohnung Niederösterreich – besenrein übergeben"
                  className="w-full h-full object-cover" loading="lazy" />
              </div>
            </div>
          </div>
        </section>

        {/* LEISTUNGEN IM ÜBERBLICK */}
        <section className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Was wir in NÖ anbieten</p>
              <h2 className="cabinet text-3xl font-bold text-[#1A1A2E]">Alle Leistungen – auch in Niederösterreich</h2>
              <p className="text-gray-500 text-sm mt-2">Von der Kellerräumung bis zur vollständigen Haushaltsauflösung – in ganz NÖ verfügbar.</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {SERVICES_NOE.map((s) => (
                <Link key={s.slug} to={s.slug}
                  className="bg-white rounded-2xl p-6 border-2 border-gray-100 hover:border-[#E8722A] hover:shadow-md transition-all group">
                  <h3 className="font-bold text-[#1A1A2E] text-base mb-2">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-[#E8722A] font-bold text-xs group-hover:gap-2 transition-all">
                    Mehr erfahren <ArrowRight size={12} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICE-GEBIETE */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-3">Unser Einzugsgebiet</p>
                <h2 className="cabinet text-3xl lg:text-4xl font-bold text-[#1A1A2E] mb-5 leading-tight">
                  Wir räumen in diesen<br />Gemeinden & Städten
                </h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-6">
                  Unser Team aus Wien ist schnell in ganz Niederösterreich. Hier sind einige der Orte, die wir regelmäßig bedienen – ist Ihr Ort nicht dabei, einfach anfragen. Wir finden einen Weg.
                </p>
                <div className="flex flex-wrap gap-2">
                  {NOE_CITIES.map((city) => (
                    <span key={city}
                      className="inline-flex items-center gap-1.5 bg-[#FAFAF8] border border-gray-200 text-[#1A1A2E] text-xs font-semibold px-3 py-1.5 rounded-full">
                      <MapPin size={10} className="text-[#E8722A]" /> {city}
                    </span>
                  ))}
                </div>
                <p className="text-gray-400 text-xs mt-4 italic">+ viele weitere Gemeinden in NÖ auf Anfrage</p>
              </div>
              <div>
                {/* Benefits */}
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { icon: <Shield size={20} />, title: 'Fixpreisgarantie', desc: 'Verbindlicher Preis nach Besichtigung – kein Stundenlohn, kein Aufpreis für Anfahrt.' },
                    { icon: <Clock size={20} />, title: 'Schnelle Termine', desc: 'Gerade in NÖ reagieren wir rasch. Notfälle werden bevorzugt eingereiht.' },
                    { icon: <Tag size={20} />, title: 'Wertanrechnung', desc: 'Gut erhaltene Möbel werden vor Ort bewertet und vom Preis abgezogen.' },
                    { icon: <CheckCircle size={20} />, title: 'Besenreine Übergabe', desc: 'Jedes Objekt wird nach der Räumung sauber übergeben – immer, ohne Aufpreis.' },
                  ].map((b) => (
                    <div key={b.title} className="flex items-start gap-4 p-4 bg-[#FAFAF8] rounded-xl border border-gray-100">
                      <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#E8722A]/10 flex items-center justify-center text-[#E8722A]">
                        {b.icon}
                      </span>
                      <div>
                        <h3 className="font-bold text-[#1A1A2E] text-sm mb-1">{b.title}</h3>
                        <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-8">
              <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Transparente Kalkulation</p>
              <h2 className="cabinet text-3xl font-bold text-[#1A1A2E]">Preise für NÖ – keine Überraschungen</h2>
              <p className="text-gray-500 text-sm mt-2">Anfahrt inklusive. Verbindlicher Fixpreis nach kostenloser Besichtigung.</p>
            </div>
            <div className="space-y-3.5 mb-8">
              {[
                { label: 'Keller / Dachboden / kleines Objekt', price: 'ab €290', highlight: false },
                { label: 'Wohnung / Büro / mittleres Objekt', price: 'ab €490', highlight: true },
                { label: 'Haus / großes Objekt', price: 'ab €890', highlight: false },
              ].map((pr) => (
                <div key={pr.label}
                  className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all hover:-translate-y-0.5 ${pr.highlight ? 'border-[#E8722A] bg-[#E8722A]/5 shadow-sm' : 'border-gray-100 bg-white'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${pr.highlight ? 'bg-[#E8722A] text-white' : 'bg-[#1A1A2E]/10 text-[#1A1A2E]'}`}>
                      <CheckCircle size={18} />
                    </div>
                    <div>
                      <div className="font-black text-[#1A1A2E] text-sm">{pr.label}</div>
                      <div className="text-gray-400 text-xs flex items-center gap-1">
                        <CheckCircle size={10} className="text-green-500" /> Anfahrt & Entsorgung inklusive
                      </div>
                    </div>
                  </div>
                  <div className="font-black text-2xl sm:text-4xl text-[#E8722A] leading-none">{pr.price}</div>
                </div>
              ))}
            </div>
            <a href="#kontakt"
              onClick={(e) => { e.preventDefault(); document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="block w-full text-center bg-[#E8722A] hover:bg-[#d4651e] text-white font-black py-4 rounded-xl transition-colors text-base">
              Preis für Ihr Objekt in NÖ anfragen
            </a>
            <p className="text-xs text-gray-400 mt-3 text-center">* Exakter Fixpreis nach kostenloser Besichtigung vor Ort. Anfahrt immer inklusive.</p>
          </div>
        </section>

        {/* REVIEWS */}
        <section className="py-12 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { name: 'Klaus H.', loc: 'Klosterneuburg', text: 'Schnell, sauber und fair. Keller geräumt in einem halben Tag. Kein Aufpreis für die Anfahrt – alles im Fixpreis.', stars: 5 },
                { name: 'Sabine W.', loc: 'Mödling', text: 'Nachlassräumung meiner Eltern in Baden. Respektvoller Umgang, alles war in 6 Stunden erledigt. Sehr empfehlenswert.', stars: 5 },
                { name: 'Gerhard F.', loc: 'Wiener Neustadt', text: 'Wohnungsräumung zum Fixpreis – keine Überraschungen. Das Team war pünktlich, professionell und hat besenrein übergeben.', stars: 5 },
              ].map((r) => (
                <div key={r.name} className="bg-[#FAFAF8] rounded-2xl p-6 border border-gray-100">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: r.stars }).map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-600 text-sm italic mb-4">"{r.text}"</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#1A1A2E] rounded-full flex items-center justify-center text-white text-xs font-black">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-black text-[#1A1A2E] text-sm">{r.name}</div>
                      <div className="text-gray-400 text-xs flex items-center gap-1"><MapPin size={10} /> {r.loc}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 lg:py-20 bg-[#FAFAF8]">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-10">
              <p className="text-[#E8722A] font-bold text-sm uppercase tracking-wider mb-2">Häufige Fragen</p>
              <h2 className="cabinet text-3xl font-bold text-[#1A1A2E]">FAQ – Räumung Niederösterreich</h2>
            </div>
            <div className="space-y-2">
              {FAQS.map((item, idx) => (
                <div key={idx} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-[#FAFAF8]/70 transition-colors">
                    <span className="font-black text-[#1A1A2E] text-sm sm:text-base leading-snug">{item.q}</span>
                    {openFaq === idx
                      ? <ChevronUp size={18} className="text-[#E8722A] flex-shrink-0" />
                      : <ChevronDown size={18} className="text-gray-400 flex-shrink-0" />}
                  </button>
                  <div className={`faq-content ${openFaq === idx ? 'open' : ''}`}>
                    <div className="px-6 pb-5 border-t border-gray-50">
                      <p className="text-gray-500 text-sm leading-relaxed pt-4">{item.a}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-14 lg:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="cabinet text-3xl sm:text-4xl font-bold text-[#1A1A2E] mb-5 leading-tight">
              Räumung in Niederösterreich?{' '}
              <span className="text-[#E8722A]">Wir sind morgen da.</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4 mb-8 text-gray-500 text-sm">
              {['Kein Anfahrtsaufpreis', 'Fixpreisgarantie', 'Besenreine Übergabe'].map((t) => (
                <span key={t} className="flex items-center gap-2">
                  <CheckCircle size={15} className="text-[#7C9A82]" />
                  <span className="font-semibold">{t}</span>
                </span>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a href="#kontakt"
                onClick={(e) => { e.preventDefault(); document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="bg-[#E8722A] hover:bg-[#d4651e] text-white font-black px-10 py-4 rounded-xl text-lg transition-colors shadow-md">
                Jetzt kostenlos anfragen
              </a>
              <a href={PHONE_LINK}
                className="flex items-center justify-center gap-2.5 text-[#1A1A2E] border-2 border-gray-300 hover:border-[#E8722A] font-black px-8 py-4 rounded-xl text-lg transition-colors">
                <Phone size={20} /> {PHONE}
              </a>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
